import React from 'react';
import { useTranslate, useDeleteMany, useMany } from "@pankod/refine-core";

import {
  useTable,
  List,
  Table,
  DateField,
  useModal,
  Button,
  Space,
  CreateButton,
  ShowButton,
  EditButton,
  DeleteButton,
  Modal,
  Typography,
  Checkbox,
  FilterDropdown,
  getDefaultFilter,
  Input,
  getDefaultSortOrder,
  useSelect,
  TextField,
  Select,
  NumberField,
  UrlField,
  Tag,
} from "@pankod/refine-antd";

import { IAnswer, IQuestion, IReview } from 'interfaces';

export const AnswerList = () => {
  const translate = useTranslate();
  const { tableProps, sorter, tableQueryResult } = useTable<IAnswer>({
    initialSorter: [
      {
        field: 'id',
        order: 'asc',
      },
    ],
  });

  const defaultColumns = React.useMemo(
    () => [
      {
        label: translate('answers.fields.id'),
        value: 'id',
      },
      {
        label: translate('answers.fields.rating'),
        value: 'rating',
      },
      {
        label: translate('answers.fields.question.title'),
        value: 'question_id',
      },
      {
        label: translate('answers.fields.review.title'),
        value: 'review_id',
      },
      {
        label: translate('answers.fields.created_at'),
        value: 'created_at',
      },
      {
        label: translate('answers.fields.updated_at'),
        value: 'updated_at',
      },
    ],
    [],
  );
  const { modalProps: columnsModalProps, show: columnsShow } = useModal();
  const questionIds = tableProps?.dataSource?.map(item => item.question_id) ?? [];
  const { data: questionData, isLoading } = useMany<IQuestion>({
    resource: 'questions',
    ids: questionIds,
    queryOptions: {
      enabled: questionIds.length > 0,
    },
  });
  const { selectProps: questionSelectProps } = useSelect<IQuestion>({
    resource: 'questions',
    optionLabel: 'content',
    optionValue: 'id',
  });

  const reviewIds = tableProps?.dataSource?.map(item => item.review_id) ?? [];
  const { data: reviewData, isLoading: reviewIsLoading } = useMany<IReview>({
    resource: 'reviews',
    ids: reviewIds,
    queryOptions: {
      enabled: reviewIds.length > 0,
    },
  });
  const { selectProps: reviewSelectProps } = useSelect<IReview>({
    resource: 'reviews',
    optionLabel: 'id',
    optionValue: 'id',
  });

  const [hiddenColumns, setHiddenColumns] = React.useState<string[]>(['created_at', 'updated_at', 'id']);

  const [selectedRowKeys, setSelectedRowKeys] = React.useState<React.Key[]>([]);
  const { mutate, isSuccess, isLoading: deleteManyIsLoading } = useDeleteMany<IAnswer>();
  const deleteSelectedItems = () => {
    mutate({
      resource: 'answers',
      ids: selectedRowKeys.map(String),
    });
  };
  const onSelectChange = (selectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(selectedRowKeys);
  };
  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    selections: [Table.SELECTION_ALL, Table.SELECTION_INVERT, Table.SELECTION_NONE],
  };
  const hasSelected = selectedRowKeys.length > 0;
  const handleHideColumn = (value: string, isChecked: boolean) => {
    if (isChecked) setHiddenColumns(prev => prev.filter(col => col !== value));
    else setHiddenColumns(prev => [...prev, value]);
  };
  const columns = React.useMemo(
    () => [
      <Table.Column<IAnswer>
        key='id'
        dataIndex='id'
        title={translate('answers.fields.id')}
        sorter
        defaultSortOrder={getDefaultSortOrder('id', sorter)}
      />,
      <Table.Column<IAnswer>
        key='rating'
        dataIndex='rating'
        title={translate('answers.fields.rating')}
        render={value => <NumberField value={value} />}
        sorter
      />,
      <Table.Column
        key='question_id'
        dataIndex='question_id'
        title={translate('answers.fields.question.title')}
        render={(value: string) => {
          if (isLoading) {
            return <TextField value={translate('loading')} />;
          }
          return <TextField value={questionData?.data.find((item: IQuestion) => item.id === value)?.content} />;
        }}
        filterDropdown={props => (
          <FilterDropdown {...props}>
            <Select
              mode='multiple'
              style={{ minWidth: 200 }}
              placeholder={translate('answers.fields.question.filters.placeholder')}
              {...questionSelectProps}
            />
          </FilterDropdown>
        )}
      />,
      <Table.Column
        key='review_id'
        dataIndex='review_id'
        title={translate('answers.fields.review.title')}
        render={(value: string) => {
          if (reviewIsLoading) {
            return <TextField value={translate('loading')} />;
          }
          return (
            <UrlField value={'/reviews/show/' + reviewData?.data.find(item => item.id === value)?.id}>
              {reviewData?.data.find(item => item.id === value)?.id}
            </UrlField>
          );
        }}
        filterDropdown={props => (
          <FilterDropdown {...props}>
            <Select
              mode='multiple'
              style={{ minWidth: 200 }}
              placeholder={translate('answers.fields.review.filters.placeholder')}
              {...reviewSelectProps}
            />
          </FilterDropdown>
        )}
      />,
      <Table.Column<IAnswer>
        dataIndex='created_at'
        title={translate('answers.fields.created_at')}
        render={value => <DateField format='LLL' value={value} />}
        sorter
      />,
      <Table.Column<IAnswer>
        dataIndex='updated_at'
        title={translate('answers.fields.updated_at')}
        render={value => <DateField format='LLL' value={value} />}
        sorter
      />,
    ],
    [tableProps, sorter, questionData, questionSelectProps, reviewData, reviewSelectProps, reviewIsLoading, isLoading],
  );
  const visibleColumns = React.useMemo(() => {
    return columns.filter(column => !hiddenColumns.includes(column.props.dataIndex));
  }, [hiddenColumns, columns]);

  React.useEffect(() => {
    if (isSuccess) {
      setSelectedRowKeys([]);
    }
  }, [isSuccess]);

  return (
    <>
      <List
        pageHeaderProps={{
          subTitle: hasSelected && (
            <Button danger onClick={deleteSelectedItems} disabled={!hasSelected} loading={deleteManyIsLoading}>
              {`Elimina ${selectedRowKeys.length} oggetti selezionati`}
            </Button>
          ),
          extra: (
            <Space>
              <Tag color='#f1c531'>
                {translate('labels.totalNumber', { resource: translate('answers.answers').toLowerCase() })}
                {tableQueryResult.data?.total || ''}
              </Tag>
              <CreateButton />
              <Button onClick={columnsShow}>{translate('table.hideColumns.buttonTitle')}</Button>
            </Space>
          ),
        }}>
        <Table<IAnswer>
          rowSelection={rowSelection}
          {...tableProps}
          size='small'
          scroll={{ y: '60vh', x: 10 * visibleColumns.length + 'vw' }}
          pagination={{ ...tableProps.pagination, position: ['topLeft'], size: 'small' }}
          rowKey='id'>
          <Table.Column<IAnswer>
            title=''
            dataIndex='actions'
            render={(_text, record): React.ReactNode => {
              return (
                <Space>
                  <ShowButton title={translate('buttons.show')} size='small' recordItemId={record.id} hideText />
                  <EditButton title={translate('buttons.edit')} size='small' recordItemId={record.id} hideText />
                  <DeleteButton title={translate('buttons.delete')} size='small' recordItemId={record.id} hideText />
                </Space>
              );
            }}
          />
          {visibleColumns}
        </Table>
      </List>
      <Modal footer={null} {...columnsModalProps}>
        <Space direction='vertical'>
          <Typography.Text>{translate('table.hideColumns.title')}</Typography.Text>
          {defaultColumns.map(column => (
            <Checkbox
              key={column.value}
              checked={!hiddenColumns.includes(column.value)}
              onChange={e => handleHideColumn(column.value, e.target.checked)}>
              {column.label}
            </Checkbox>
          ))}
        </Space>
      </Modal>
    </>
  );
};
